import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/**
 * Roraima's hero board (owner, 2026-10-16: the tepui far off at sunset, its waterfall, a sea of
 * cloud): the sky goes down from a dark plum through the colour circle's lavender and pink and
 * terracotta to a gold glow low behind the mountains; Mount Roraima stands there, a table of rock with
 * a flat top and sheer walls, the sun low behind it; a thin waterfall falls from Roraima's wall into the cloud, and the sea of cloud, lit pink and gold
 * on top, drifts slowly round their feet. A gust (the pointer, a finger, the counter counting) warms
 * the sunset and the clouds. Laid out by the stage's height, so a wide screen does not stretch it.
 */
@Component( {
  selector: 'app-hero-roraima',
  template: '<canvas #canvas class="hr-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hr-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroRoraimaComponent extends HeroMotifBase {

  private back: HTMLCanvasElement | null = null;
  private clouds: HTMLCanvasElement | null = null;
  // the rain: a pool of drops, each falling only while the gust is strong enough for its rank
  private drops: { x: number; y: number; len: number; speed: number; rank: number; alpha: number }[] = [];
  // the spray rising where the waterfall meets the cloud (owner): puffs that rise, swell and fade
  private puffs: { x: number; y: number; vx: number; vy: number; age: number; life: number; r: number }[] = [];
  private puffDue = 0;
  // how far the waterfall's light has run: it runs faster in a gust (owner)
  private flow = 0;
  private scaleK = 1;
  private seedScene = 1;
  // where the sun sits and where the waterfall falls, in page pixels
  private sun = { x: 0, y: 0 };
  private fall = { x: 0, rim: 0, top: 0, bottom: 0 };

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
    this.clouds = null;
  }

  protected build( rand: () => number ): void {
    this.scaleK = Math.max( 0.55, Math.min( 1.25, this.height / 760 ) );
    this.seedScene = Math.floor( rand() * 1e6 );
    this.back = null;
    this.clouds = null;
    this.drops = [];
    const k = this.scaleK;
    for ( let i = 0; i < 520; i++ ) {
      this.drops.push( {
        x: rand() * this.width, y: rand() * this.height, len: ( 14 + rand() * 22 ) * k,
        speed: ( 700 + rand() * 500 ) * k, rank: rand(), alpha: 0.12 + rand() * 0.2,
      } );
    }
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    const scale = ctx.getTransform().a;
    // the page's wallpaper: the rain, still, all down the page (owner)
    if ( this.frozen ) {
      if ( !this.back ) { this.bakeRainWall( scale ); }
      if ( this.back ) { ctx.drawImage( this.back, 0, 0, W, H ); }
      return;
    }
    if ( !this.back ) { this.bake( scale ); }
    if ( !this.back ) { return; }
    ctx.drawImage( this.back, 0, 0, W, H );
    const e = this.energy;
    const [ , terracotta, gold, pink ] = this.five();
    // the sun behind the mountains, steady (owner: no burst of light in the heavy rain)
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const R = Math.max( W, H );
    const glow = ctx.createRadialGradient( this.sun.x, this.sun.y, 0, this.sun.x, this.sun.y, R * 0.35 );
    glow.addColorStop( 0, this.rgba( this.mix( gold, [ 255, 230, 180 ], 0.3 ), 0.18 ) );
    glow.addColorStop( 0.4, this.rgba( terracotta, 0.06 ) );
    glow.addColorStop( 1, this.rgba( pink, 0 ) );
    ctx.fillStyle = glow;
    ctx.fillRect( 0, 0, W, H );
    ctx.restore();
    // the waterfall: light running down its thread, never still
    // its light runs down faster in a gust (owner)
    this.flow = ( this.flow + dt * 0.12 * ( 1 + 2.5 * e ) ) % 1;
    this.drawFall( ctx );
    // the spray under the cloud, the cloud drifting over it (owner)
    this.drawSpray( ctx, dt );
    // the sea of cloud, drifting right and wrapping round
    if ( this.clouds ) {
      // whole pixels, so the seam between the two copies never shows as a line
      const off = Math.round( ( performance.now() / 1000 * 10 * this.scaleK ) % W );
      ctx.globalAlpha = 1;
      ctx.drawImage( this.clouds, off, 0, W, H );
      ctx.drawImage( this.clouds, off - W, 0, W, H );
    }
    this.drawRain( ctx, dt );
  }

  /**
   * The spray (owner): a waterfall's mist, not glowing puffs. Where the water plunges into the cloud a
   * column of soft white mist billows up, dense at its foot, widening and thinning as it climbs,
   * spreading out both ways; slow, many overlapping wisps, painted like the cloud itself.
   */
  private drawSpray( ctx: CanvasRenderingContext2D, dt: number ): void {
    const k = this.scaleK;
    const H = this.height;
    const e = this.energy;
    const landY = H * 0.765;
    if ( dt > 0 ) {
      this.puffDue += dt * ( 14 + 16 * e );
      while ( this.puffDue >= 1 ) {
        this.puffDue -= 1;
        this.puffs.push( {
          // spreading both ways round the plunge, not leaning off to one side (owner)
          x: this.fall.x + ( Math.random() - 0.5 ) * 22 * k, y: landY + Math.random() * 8 * k,
          vx: ( Math.random() - 0.5 ) * 18 * k, vy: -( 10 + Math.random() * 12 ) * k * ( 1 + 0.4 * e ),
          age: 0, life: 6 + Math.random() * 4, r: ( 10 + Math.random() * 8 ) * k,
        } );
      }
      for ( const p of this.puffs ) {
        p.age += dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy *= Math.pow( 0.8, dt );
      }
      this.puffs = this.puffs.filter( p => p.age < p.life );
    }
    const [ plum, , , pink, lavender ] = this.five();
    const tone = this.mix( this.mix( lavender, pink, 0.35 ), [ 255, 255, 255 ], 0.35 );
    const shade = this.mix( lavender, plum, 0.2 );
    for ( const p of this.puffs ) {
      const f = p.age / p.life;
      const r = p.r * ( 1 + 3 * f );
      const a = 0.11 * Math.min( 1, f * 4 ) * Math.pow( 1 - f, 1.3 );
      if ( a < 0.004 ) { continue; }
      // lit on top, a little shaded below, like the cloud's billows
      const g = ctx.createRadialGradient( p.x, p.y - r * 0.25, 0, p.x, p.y, r );
      g.addColorStop( 0, this.rgba( tone, a ) );
      g.addColorStop( 0.6, this.rgba( this.mix( tone, shade, 0.4 ), a * 0.6 ) );
      g.addColorStop( 1, this.rgba( shade, 0 ) );
      ctx.fillStyle = g;
      ctx.fillRect( p.x - r, p.y - r, r * 2, r * 2 );
    }
  }

  /**
   * The rain (owner): a light drizzle at rest; heavier while the counter counts and under the
   * pointer, every drop a thin pale streak slanting a little with the wind.
   */
  private drawRain( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    const e = this.energy;
    const [ , , , pink, lavender ] = this.five();
    const tone = this.mix( this.mix( lavender, pink, 0.3 ), [ 255, 255, 255 ], 0.55 );
    const share = 0.12 + 0.88 * e;
    const slant = 0.18;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = this.rgba( tone, 1 );
    ctx.lineWidth = Math.max( 0.8, 1.1 * this.scaleK );
    ctx.lineCap = 'round';
    for ( const d of this.drops ) {
      const v = d.speed * ( 1 + 0.4 * e );
      d.y += v * dt;
      d.x += v * slant * dt;
      if ( d.y - d.len > H ) { d.y = -d.len - Math.random() * H * 0.2; d.x = Math.random() * ( W + H * slant ) - H * slant; }
      if ( d.x > W + 20 ) { d.x -= W + H * slant; }
      if ( d.rank > share ) { continue; }
      ctx.globalAlpha = d.alpha * ( 0.7 + 0.5 * e );
      ctx.beginPath();
      ctx.moveTo( d.x, d.y );
      ctx.lineTo( d.x - d.len * slant, d.y - d.len );
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * The waterfall, like the owner's photo of Roraima: it breaks out of a cleft a little below the
   * rim, falls straight down the wall, white, widening as it goes, light running down it, and bursts
   * into spray where it reaches the foot of the wall.
   */
  private drawFall( ctx: CanvasRenderingContext2D ): void {
    const k = this.scaleK;
    const { x, top, bottom } = this.fall;
    const [ , , gold, pink ] = this.five();
    const water = this.mix( this.mix( pink, gold, 0.3 ), [ 255, 255, 255 ], 0.7 );
    const len = bottom - top;
    // a little wider (owner)
    const w0 = 2.6 * k;
    const w1 = 9 * k;
    ctx.save();
    // the cleft it comes out of: a dark notch from the rim down to where the water starts
    ctx.strokeStyle = this.rgba( [ 20, 12, 16 ], 0.35 );
    ctx.lineWidth = 4.2 * k;
    ctx.lineCap = 'butt';
    ctx.beginPath();
    ctx.moveTo( x, this.fall.rim );
    ctx.lineTo( x, top + 2 * k );
    ctx.stroke();
    ctx.globalCompositeOperation = 'lighter';
    // the sheet of water, narrow at the cleft, wider below, fading a little as it falls
    const sheet = ctx.createLinearGradient( 0, top, 0, bottom );
    sheet.addColorStop( 0, this.rgba( water, 0.55 ) );
    sheet.addColorStop( 0.7, this.rgba( water, 0.4 ) );
    sheet.addColorStop( 1, this.rgba( water, 0.2 ) );
    ctx.fillStyle = sheet;
    ctx.beginPath();
    ctx.moveTo( x - w0 / 2, top );
    ctx.lineTo( x + w0 / 2, top );
    ctx.lineTo( x + w1 / 2, bottom );
    ctx.lineTo( x - w1 / 2, bottom );
    ctx.closePath();
    ctx.fill();
    // light running down it, faster in a gust
    for ( let i = 0; i < 6; i++ ) {
      const t = ( this.flow + i / 6 ) % 1;
      const y = top + len * t;
      const w = w0 + ( w1 - w0 ) * t;
      const g = ctx.createLinearGradient( 0, y - 16 * k, 0, y + 16 * k );
      g.addColorStop( 0, this.rgba( water, 0 ) );
      g.addColorStop( 0.5, this.rgba( water, 0.35 ) );
      g.addColorStop( 1, this.rgba( water, 0 ) );
      ctx.fillStyle = g;
      ctx.fillRect( x - w / 2, Math.max( top, y - 16 * k ), w, 32 * k );
    }
    // the spray at the foot of the wall
    const e = this.energy;
    const r = ( 34 + 14 * e ) * k;
    ctx.save();
    ctx.translate( x, bottom - 4 * k );
    ctx.scale( 1.3, 0.8 );
    const mist = ctx.createRadialGradient( 0, 0, 0, 0, 0, r );
    mist.addColorStop( 0, this.rgba( water, 0.4 ) );
    mist.addColorStop( 0.4, this.rgba( water, 0.15 ) );
    mist.addColorStop( 1, this.rgba( water, 0 ) );
    ctx.fillStyle = mist;
    ctx.fillRect( -r, -r, 2 * r, 2 * r );
    ctx.restore();
    ctx.restore();
  }

  /**
   * The still picture, painted once: the sunset sky, a few soft wisps of cloud lit from below, the far
   * hills, Roraima with its walls in shadow, the faint grain of the
   * walls; and, on its own layer, the sea of cloud.
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const UW = Math.min( W, H * 2.1 );
    const cx = W / 2;
    const toX = ( u: number ) => cx + u * UW;
    // a fixed seed: the mountain keeps one shape on every screen (owner)
    const rand = this.seededLocal( 5 );
    const [ plum, terracotta, gold, pink, lavender ] = this.five();
    const night: RGB = [ 14, 10, 12 ];
    const c = document.createElement( 'canvas' );
    c.width = Math.ceil( W * scale );
    c.height = Math.ceil( H * scale );
    const g = c.getContext( '2d' )!;
    g.scale( scale, scale );
    const horizon = H * 0.74;

    // the sky: dark plum above, lavender, pink and terracotta, gold low down
    const sky = g.createLinearGradient( 0, 0, 0, horizon );
    sky.addColorStop( 0, this.rgba( this.mix( plum, night, 0.45 ), 1 ) );
    sky.addColorStop( 0.38, this.rgba( this.mix( this.mix( plum, lavender, 0.45 ), night, 0.35 ), 1 ) );
    sky.addColorStop( 0.62, this.rgba( this.mix( this.mix( lavender, pink, 0.55 ), night, 0.3 ), 1 ) );
    sky.addColorStop( 0.82, this.rgba( this.mix( this.mix( pink, terracotta, 0.6 ), night, 0.2 ), 1 ) );
    sky.addColorStop( 1, this.rgba( this.mix( terracotta, gold, 0.55 ), 1 ) );
    g.fillStyle = sky;
    g.fillRect( 0, 0, W, H );
    // the setting sun, partly behind the mountain's right end, half down behind the far ranges (owner)
    this.sun = { x: toX( 0.36 ), y: horizon - H * 0.06 };
    const sunR = H * 0.032;
    const halo = g.createRadialGradient( this.sun.x, this.sun.y, 0, this.sun.x, this.sun.y, sunR * 5 );
    halo.addColorStop( 0, this.rgba( this.mix( gold, [ 255, 240, 200 ], 0.4 ), 0.55 ) );
    halo.addColorStop( 0.25, this.rgba( this.mix( gold, terracotta, 0.4 ), 0.25 ) );
    halo.addColorStop( 1, this.rgba( terracotta, 0 ) );
    g.fillStyle = halo;
    g.fillRect( this.sun.x - sunR * 5, this.sun.y - sunR * 5, sunR * 10, sunR * 10 );
    const disc = g.createRadialGradient( this.sun.x, this.sun.y, 0, this.sun.x, this.sun.y, sunR );
    disc.addColorStop( 0, this.rgba( this.mix( gold, [ 255, 245, 215 ], 0.55 ), 1 ) );
    disc.addColorStop( 0.85, this.rgba( this.mix( gold, [ 255, 235, 190 ], 0.3 ), 0.95 ) );
    disc.addColorStop( 1, this.rgba( gold, 0 ) );
    g.fillStyle = disc;
    g.beginPath();
    g.arc( this.sun.x, this.sun.y, sunR, 0, Math.PI * 2 );
    g.fill();
    // a few long wisps of cloud high up, lit pink from below
    for ( let i = 0; i < 9; i++ ) {
      const x = rand() * W;
      const y = H * ( 0.12 + rand() * 0.4 );
      const rx = UW * ( 0.12 + rand() * 0.2 );
      const ry = H * ( 0.008 + rand() * 0.014 );
      const tone = this.mix( this.mix( pink, gold, rand() * 0.5 ), night, 0.15 + ( 1 - y / horizon ) * 0.4 );
      g.save();
      g.translate( x, y );
      g.rotate( ( rand() - 0.5 ) * 0.06 );
      g.scale( rx, ry );
      const wg = g.createRadialGradient( 0, 0.3, 0, 0, 0, 1 );
      wg.addColorStop( 0, this.rgba( tone, 0.32 ) );
      wg.addColorStop( 1, this.rgba( tone, 0 ) );
      g.fillStyle = wg;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }

    // the far ranges along the horizon, two layers, hazier farther off: ridges of several sizes of
    // wave with sharp crests (folded waves), a jagged fine edge, so they read as mountains, not a wave
    const hillTone = this.mix( this.mix( lavender, plum, 0.5 ), this.mix( pink, terracotta, 0.5 ), 0.35 );
    const range = ( baseY: number, amp: number, tone: RGB, seedOff: number ) => {
      const rr = this.seededLocal( this.seedScene + seedOff );
      const waves = [ 0, 1, 2, 3, 4 ].map( i => ( { f: 0.0016 * Math.pow( 2.1, i ) / k, p: rr() * 6.28, a: Math.pow( 0.5, i ) } ) );
      let jag = 0;
      g.fillStyle = this.rgba( tone, 1 );
      g.beginPath();
      g.moveTo( -10, H );
      for ( let x = -10; x <= W + 10; x += 3 ) {
        let h = 0;
        for ( const w of waves ) { h += w.a * ( 1 - Math.abs( Math.sin( x * w.f + w.p ) ) ); }
        jag = jag * 0.7 + ( rr() - 0.5 ) * 0.3;
        g.lineTo( x, baseY - amp * ( h / 1.94 + jag * 0.06 ) );
      }
      g.lineTo( W + 10, H );
      g.closePath();
      g.fill();
    };
    range( horizon - H * 0.035, H * 0.07, this.mix( hillTone, this.mix( pink, gold, 0.4 ), 0.3 ), 11 );
    range( horizon + H * 0.005, H * 0.06, hillTone, 12 );

    // a tepui: a flat top with a slightly broken rim, sheer walls, scree slopes at its ends falling
    // concave into the cloud
    const tepui = ( u0: number, u1: number, top: number, wallFoot: number, haze: number, lit: number ) => {
      const x0 = toX( u0 );
      const x1 = toX( u1 );
      // like the real one: under the sheer wall a long slope of scree and forest falls away to each
      // side, steep under the wall and easing out below (concave)
      const foot = H * 0.86;
      const slope = ( side: -1 | 1, len: number ): [ number, number ][] => {
        const out: [ number, number ][] = [];
        const n = 40;
        for ( let i = 0; i <= n; i++ ) {
          const f = i / n; // 0 at the wall's foot, 1 far out
          // one smooth concave curve (owner): steep under the wall, rounding out below
          const drop = 1 - Math.pow( 1 - f, 2.4 );
          out.push( [ ( side < 0 ? x0 : x1 ) + side * ( UW * 0.004 + len * f ), wallFoot + ( foot - wallFoot ) * drop ] );
        }
        return out;
      };
      const wall = ( x: number, side: -1 | 1 ): [ number, number ][] => {
        // the wall, sheer, a little uneven, leaning back a touch toward the top
        const out: [ number, number ][] = [];
        for ( let i = 1; i < 4; i++ ) {
          const f = i / 4;
          out.push( [ x + side * ( UW * 0.004 * ( 1 - f ) + ( rand() - 0.5 ) * UW * 0.002 ), wallFoot - ( wallFoot - top - H * 0.012 ) * f ] );
        }
        return out;
      };
      const pts: [ number, number ][] = [];
      pts.push( ...slope( -1, UW * 0.26 ).reverse() );
      pts.push( ...wall( x0, -1 ) );
      // the top, flat, its rim nicked here and there
      const steps = 80;
      for ( let i = 0; i <= steps; i++ ) {
        const x = x0 + ( x1 - x0 ) * i / steps;
        const nick = rand() < 0.12 ? H * 0.006 * rand() : 0;
        // the rim lies flat where the waterfall leaves it, so its cleft sits true (owner)
        const away = Math.min( 1, Math.max( 0, ( Math.abs( x - ( x0 + ( x1 - x0 ) * 0.78 ) ) - UW * 0.02 ) / ( UW * 0.02 ) ) );
        pts.push( [ x, top + ( away >= 1 ? nick : 0 ) + Math.sin( x * 0.01 ) * H * 0.002 * away ] );
      }
      pts.push( ...wall( x1, 1 ).reverse() );
      pts.push( ...slope( 1, UW * 0.28 ) );
      const outline = () => {
        g.beginPath();
        pts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
        g.closePath();
      };
      // a touch darker (owner)
      const rock = this.mix( this.mix( this.mix( plum, [ 60, 36, 40 ], 0.4 ), hillTone, haze ), night, 0.18 );
      outline();
      const rg = g.createLinearGradient( 0, top, 0, foot );
      rg.addColorStop( 0, this.rgba( this.mix( rock, terracotta, 0.12 * lit ), 1 ) );
      rg.addColorStop( 0.5, this.rgba( rock, 1 ) );
      rg.addColorStop( 1, this.rgba( this.mix( rock, night, 0.3 * ( 1 - haze ) ), 1 ) );
      g.fillStyle = rg;
      g.fill();
      // the slopes under the walls, as in photos of the real one: buttresses of scree and forest running
      // down the fall line, parted by gullies, each catching a faint warm light on its sun side (right)
      // and in shade on the other; painted at half size so they come out soft, then lost in the violet
      // haze lower down
      const surfaceAt = ( x: number ) => {
        let y = foot;
        for ( let i = 1; i < pts.length; i++ ) {
          const [ ax, ay ] = pts[ i - 1 ], [ bx, by ] = pts[ i ];
          const lo = Math.min( ax, bx ), hi = Math.max( ax, bx );
          if ( lo <= x && x <= hi ) { y = Math.min( y, ay + ( by - ay ) * ( x - ax ) / ( bx - ax || 1 ) ); }
        }
        return Math.max( y, wallFoot );
      };
      const ss = 0.5;
      const sc = document.createElement( 'canvas' );
      sc.width = Math.ceil( W * scale * ss );
      sc.height = Math.ceil( H * scale * ss );
      const sg = sc.getContext( '2d' )!;
      sg.scale( scale * ss, scale * ss );
      const litFace = this.mix( this.mix( rock, terracotta, 0.35 ), pink, 0.15 );
      const darkFace = this.mix( rock, night, 0.45 );
      const sx0 = x0 - UW * 0.24;
      const sx1 = x1 + UW * 0.26;
      // many small ridges all over the apron rather than a few big ones (the photos: the talus is
      // wrinkled with little spurs and gullies), each a short soft wedge along the fall line
      const ridges = 160;
      for ( let i = 0; i < ridges; i++ ) {
        const x = sx0 + ( sx1 - sx0 ) * rand();
        const y0 = surfaceAt( x );
        if ( y0 >= foot - H * 0.01 ) { continue; }
        // they start anywhere on the slope, more of them high up
        const y = y0 + ( foot - y0 ) * Math.pow( rand(), 1.6 ) * 0.8;
        const lean = x < x0 ? -0.9 : x > x1 ? 0.9 : ( ( x - ( x0 + x1 ) / 2 ) / ( ( x1 - x0 ) / 2 ) ) * 0.15;
        const len = ( foot - y ) * ( 0.25 + rand() * 0.45 );
        const half = ( 0.003 + rand() * 0.007 ) * UW;
        const ex = x + lean * len;
        const ey = y + len;
        const tone0 = this.mix( litFace, gold, 0.15 * rand() );
        for ( const [ side, tone, alpha ] of [ [ 1, tone0, 0.22 ], [ -1, darkFace, 0.3 ] ] as [ number, RGB, number ][] ) {
          const lg = sg.createLinearGradient( 0, y, 0, ey );
          lg.addColorStop( 0, this.rgba( tone, 0 ) );
          lg.addColorStop( 0.25, this.rgba( tone, alpha ) );
          lg.addColorStop( 1, this.rgba( tone, 0 ) );
          sg.fillStyle = lg;
          sg.beginPath();
          sg.moveTo( x, y );
          sg.quadraticCurveTo( x + side * half * 0.5, ( y + ey ) / 2, ex + side * half, ey );
          sg.lineTo( ex, ey );
          sg.closePath();
          sg.fill();
        }
      }
      // the dark forest that hugs the foot of the walls and covers the slopes below: its upper edge
      // a little ragged, never climbing far up the wall (owner)
      const forest = this.mix( this.mix( [ 40, 62, 42 ], rock, 0.45 ), night, 0.45 );
      // its upper edge runs in S curves along the slopes, one fixed shape
      // only ever downward, so it never climbs the wall
      // (owner: more S, in and out several times, detailed): a big swing, smaller ones on it, and a
      // fine ragged edge on those
      const bend = ( x: number ) => {
        const t = ( x - sx0 ) / ( sx1 - sx0 ) * Math.PI * 2;
        // past the walls the curve goes, so the green on the end slopes starts right where the sheer
        // wall meets the slope (owner)
        // and it comes to nothing at the walls' ends, so the green's edge leaves exactly from the
        // corner where wall and slope meet (owner)
        const keep = Math.max( 0, Math.min( 1, ( x - x0 ) / ( UW * 0.05 ), ( x1 - x ) / ( UW * 0.05 ) ) );
        // and toward both ends it sits a touch higher (owner)
        const lift = 1 - 0.4 * Math.max( 0, 1 - Math.min( x - sx0, sx1 - x ) / ( ( sx1 - sx0 ) * 0.5 ) );
        // and its dip on the right half rides a little higher (owner)
        const right = 1 - 0.4 * Math.max( 0, Math.min( 1, ( x - ( x0 + x1 ) / 2 ) / ( ( x1 - x0 ) / 2 ) ) );
        return right * lift * keep * H * ( 0.018 * ( 1 + Math.sin( t * 1.5 + 0.4 ) ) + 0.009 * ( 1 + Math.sin( t * 4 + 1.7 ) )
          + 0.004 * ( 1 + Math.sin( t * 9 + 0.2 ) ) + 0.0018 * ( 1 + Math.sin( t * 23 + 2.9 ) ) );
      };
      // a solid body of it first, following the ground, so it never reads sparse (owner)
      {
        const top0 = ( x: number ) => surfaceAt( x ) + bend( x );
        // a soft gradient: darkest at its edge, thinning into the haze below
        const band = sg.createLinearGradient( 0, wallFoot, 0, wallFoot + H * 0.13 );
        band.addColorStop( 0, this.rgba( forest, 0.9 ) );
        band.addColorStop( 0.55, this.rgba( forest, 0.8 ) );
        band.addColorStop( 1, this.rgba( forest, 0.3 ) );
        sg.fillStyle = band;
        sg.beginPath();
        sg.moveTo( sx0, top0( sx0 ) );
        for ( let x = sx0; x <= sx1; x += 4 ) { sg.lineTo( x, top0( x ) ); }
        // down most of the end slopes as well (owner: dense trees on the slopes)
        for ( let x = sx1; x >= sx0; x -= 4 ) { sg.lineTo( x, Math.max( top0( x ) + H * 0.05, top0( x ) + ( foot - top0( x ) ) * 0.75 ) ); }
        sg.closePath();
        sg.fill();
      }
      // then the crowns on it, dense, and on down the end slopes too (owner)
      for ( let i = 0; i < 2800; i++ ) {
        const x = sx0 + ( sx1 - sx0 ) * rand();
        const sy = surfaceAt( x );
        const y = sy + bend( x ) - H * 0.004 + Math.pow( rand(), 1.3 ) * Math.max( H * 0.045, ( foot - sy ) * 0.7 );
        const climb = rand() < 0.1 ? H * rand() * 0.006 : 0;
        const rw = ( 2 + rand() * 4 ) * k;
        const fg = sg.createLinearGradient( 0, y - climb - rw, 0, y + H * 0.02 );
        fg.addColorStop( 0, this.rgba( forest, 0 ) );
        fg.addColorStop( 0.3, this.rgba( forest, 0.65 ) );
        fg.addColorStop( 1, this.rgba( forest, 0 ) );
        sg.fillStyle = fg;
        sg.fillRect( x - rw, y - climb - rw, rw * 2, climb + rw + H * 0.02 );
      }
      // and the grass and scrub among them in different greens (owner): moss, olive, a bluer green, a
      // yellower one, a browner one, all in the walls' shade; from a seed of their own
      const gr = this.seededLocal( 31 );
      const greens: RGB[] = [ [ 70, 96, 52 ], [ 92, 96, 52 ], [ 46, 80, 66 ], [ 106, 120, 58 ], [ 80, 72, 44 ] ]
        // darker than the forest under them (owner)
        .map( c => this.mix( this.mix( c as RGB, rock, 0.25 ), night, 0.66 ) );
      for ( let i = 0; i < 2000; i++ ) {
        const x = sx0 + ( sx1 - sx0 ) * gr();
        const sy = surfaceAt( x );
        const y = sy + bend( x ) + H * 0.002 + Math.pow( gr(), 1.4 ) * Math.max( H * 0.04, ( foot - sy ) * 0.6 );
        const r = ( 1.5 + gr() * 3 ) * k;
        const tone = greens[ Math.floor( gr() * greens.length ) ];
        const tg = sg.createRadialGradient( x, y, 0, x, y, r );
        tg.addColorStop( 0, this.rgba( tone, 0.35 + gr() * 0.25 ) );
        tg.addColorStop( 1, this.rgba( tone, 0 ) );
        sg.fillStyle = tg;
        sg.fillRect( x - r, y - r, r * 2, r * 2 );
      }
      g.save();
      outline();
      g.clip();
      g.drawImage( sc, 0, 0, Math.floor( W * scale * ss ), Math.floor( H * scale * ss ), 0, 0, W, H );
      // the haze the slopes sink into, thicker the lower they go
      const haze2 = this.mix( this.mix( lavender, plum, 0.45 ), pink, 0.1 );
      const gg = g.createLinearGradient( 0, wallFoot, 0, foot );
      gg.addColorStop( 0, this.rgba( haze2, 0 ) );
      gg.addColorStop( 1, this.rgba( haze2, 0.6 ) );
      g.fillStyle = gg;
      g.fillRect( x0 - UW * 0.3, wallFoot, x1 - x0 + UW * 0.6, foot - wallFoot );
      g.restore();
      // a strip of green along the top (owner), as the scrub on a tepui's edge looks from afar: a dark
      // band just under the rim, fading down; little tufts of bush breaking the rim's line; and here
      // and there a tongue of green hanging a short way down a cleft
      const scrub = this.mix( this.mix( [ 56, 90, 50 ], rock, 0.35 ), night, 0.1 );
      const topPts = pts.filter( ( [ , y ] ) => y < top + H * 0.012 );
      g.save();
      outline();
      g.clip();
      for ( const [ x, y ] of topPts ) {
        const d = H * ( 0.006 + rand() * 0.008 );
        const bg = g.createLinearGradient( 0, y, 0, y + d );
        bg.addColorStop( 0, this.rgba( scrub, 0.7 ) );
        bg.addColorStop( 1, this.rgba( scrub, 0 ) );
        g.fillStyle = bg;
        g.fillRect( x - ( x1 - x0 ) / 160 - 1, y - 1, ( x1 - x0 ) / 80 + 2, d + 1 );
        if ( rand() < 0.12 ) {
          const len = H * ( 0.012 + rand() * 0.03 );
          const tg = g.createLinearGradient( 0, y, 0, y + len );
          tg.addColorStop( 0, this.rgba( scrub, 0.55 ) );
          tg.addColorStop( 1, this.rgba( scrub, 0 ) );
          g.fillStyle = tg;
          g.fillRect( x - k, y, ( 1.5 + rand() * 2 ) * k, len );
        }
      }
      g.restore();
      // the tufts on the rim, away from the waterfall's flat lip
      const fallX = x0 + ( x1 - x0 ) * 0.78;
      g.fillStyle = this.rgba( this.mix( scrub, night, 0.15 ), 0.85 );
      // each sits on the rim where it is, never past the ends, so none hangs in the air (owner)
      const rimY = ( x: number ) => {
        for ( let i = 1; i < topPts.length; i++ ) {
          const [ ax, ay ] = topPts[ i - 1 ], [ bx, by ] = topPts[ i ];
          if ( ax <= x && x <= bx ) { return Math.max( ay, by ); }
        }
        return top;
      };
      for ( const [ x0t ] of topPts ) {
        const x = x0t + ( rand() - 0.5 ) * 6 * k;
        const r = ( 0.8 + rand() * 1.8 ) * k;
        const rx = r * ( 1.2 + rand() );
        if ( Math.abs( x - fallX ) < UW * 0.03 || rand() < 0.4 ) { continue; }
        if ( x - rx < x0 + UW * 0.012 || x + rx > x1 - UW * 0.012 ) { continue; }
        g.beginPath();
        g.ellipse( x, rimY( x ) + r * 0.45, rx, r, 0, Math.PI, 0 );
        g.fill();
      }
      // the walls' grain: soft, faint, uneven vertical clefts
      g.save();
      outline();
      g.clip();
      for ( let i = 0; i < 70; i++ ) {
        const x = x0 + ( x1 - x0 ) * rand();
        const len = ( wallFoot - top ) * ( 0.3 + rand() * 0.7 );
        g.strokeStyle = this.rgba( rand() < 0.6 ? this.mix( rock, night, 0.4 ) : this.mix( rock, pink, 0.25 ), 0.05 + rand() * 0.08 );
        g.lineWidth = ( 1 + rand() * 3 ) * k;
        g.beginPath();
        g.moveTo( x, top + H * 0.012 );
        g.lineTo( x + ( rand() - 0.5 ) * 4 * k, top + H * 0.012 + len );
        g.stroke();
      }
      // the wall's relief, painted soft at half size, never as lines (owner: not a grid of strokes):
      // the face is a row of rounded columns, each shaded across its width, the left side in shadow
      // and the right catching a little of the low sun; black water stains run down from the rim, as on
      // every tepui (the paler rockfall patches went: owner)
      const dr = this.seededLocal( 23 );
      const wallH = wallFoot - top;
      const rs = 0.5;
      const rc = document.createElement( 'canvas' );
      rc.width = Math.ceil( W * scale * rs );
      rc.height = Math.ceil( H * scale * rs );
      const r2 = rc.getContext( '2d' )!;
      r2.scale( scale * rs, scale * rs );
      const shadowT = this.mix( rock, night, 0.6 );
      const lightT = this.mix( this.mix( rock, terracotta, 0.35 ), pink, 0.2 );
      for ( let x = x0; x < x1; ) {
        const w = ( 6 + dr() * 18 ) * k;
        const cg = r2.createLinearGradient( x, 0, x + w, 0 );
        cg.addColorStop( 0, this.rgba( shadowT, 0.3 ) );
        cg.addColorStop( 0.35, this.rgba( shadowT, 0.06 ) );
        cg.addColorStop( 0.7, this.rgba( lightT, 0.04 ) );
        cg.addColorStop( 0.92, this.rgba( lightT, 0.16 + dr() * 0.08 ) );
        cg.addColorStop( 1, this.rgba( shadowT, 0.2 ) );
        r2.fillStyle = cg;
        r2.fillRect( x, top, w, wallH );
        x += w;
      }
      // the relief softens down the face, no edge where it ends
      r2.globalCompositeOperation = 'destination-in';
      const fade = r2.createLinearGradient( 0, top, 0, wallFoot );
      fade.addColorStop( 0, 'rgba(0,0,0,1)' );
      fade.addColorStop( 0.6, 'rgba(0,0,0,0.6)' );
      fade.addColorStop( 1, 'rgba(0,0,0,0.15)' );
      r2.fillStyle = fade;
      r2.fillRect( 0, 0, W, H );
      r2.globalCompositeOperation = 'source-over';
      for ( let i = 0; i < 18; i++ ) {
        const x = x0 + ( x1 - x0 ) * dr();
        const w = ( 3 + dr() * 8 ) * k;
        const len = wallH * ( 0.3 + dr() * 0.6 );
        const st = r2.createLinearGradient( 0, top, 0, top + len );
        st.addColorStop( 0, this.rgba( night, 0.3 + dr() * 0.15 ) );
        st.addColorStop( 1, this.rgba( night, 0 ) );
        r2.fillStyle = st;
        r2.beginPath();
        r2.moveTo( x - w / 2, top );
        r2.lineTo( x + w / 2, top );
        r2.quadraticCurveTo( x + w * 0.3, top + len * 0.6, x + ( dr() - 0.5 ) * w, top + len );
        r2.quadraticCurveTo( x - w * 0.3, top + len * 0.6, x - w / 2, top );
        r2.fill();
      }
      g.drawImage( rc, 0, 0, Math.floor( W * scale * rs ), Math.floor( H * scale * rs ), 0, 0, W, H );
      g.restore();
      // the rim's height at x, from the drawn outline itself
      const rimAt = ( x: number ) => {
        for ( let i = 1; i < pts.length; i++ ) {
          const [ ax, ay ] = pts[ i - 1 ], [ bx, by ] = pts[ i ];
          if ( ax <= x && x <= bx && ay < wallFoot && by < wallFoot ) { return ay + ( by - ay ) * ( x - ax ) / ( bx - ax || 1 ); }
        }
        return top;
      };
      return { x0, x1, top, wallFoot, rimAt };
    };
    // Roraima alone (owner: one tepui), in the middle; its sheer walls run down into the cloud (owner)
    const r = tepui( -0.32, 0.32, H * 0.44 - 10, H * 0.685 - 10, 0.15, 1 );
    // out of a cleft a little below the rim (owner's photo), on down under the cloud (owner)
    const fx = r.x0 + ( r.x1 - r.x0 ) * 0.78;
    // the cleft starts just inside the rim, so nothing of it shows above the top (owner)
    this.fall = { x: fx, rim: Math.max( r.rimAt( fx - 2 ), r.rimAt( fx ), r.rimAt( fx + 2 ) ) + 1.5, top: r.top + ( r.wallFoot - r.top ) * 0.12, bottom: H * 0.9 };

    // a clearing of haze behind the hero's words, so they read over the sky
    for ( const b of this.blocks ) {
      g.save();
      g.translate( b.x + b.w / 2, b.y + b.h / 2 );
      g.scale( b.w / 2 + 70 * k, b.h / 2 + 40 * k );
      const cl = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      cl.addColorStop( 0, this.rgba( this.mix( plum, night, 0.4 ), 0.35 ) );
      cl.addColorStop( 1, this.rgba( plum, 0 ) );
      g.fillStyle = cl;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }
    this.back = c;
    this.bakeClouds( scale );
  }

  /**
   * The sea of cloud, on a layer of its own that wraps round: a deep soft body below, its billows lit
   * pink and gold on top by the low sun and lavender in their shade, thinning upward into wisps that
   * lie against the walls.
   */
  private bakeClouds( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const rand = this.seededLocal( this.seedScene + 3 );
    const [ plum, , gold, pink, lavender ] = this.five();
    const night: RGB = [ 14, 10, 12 ];
    const c = document.createElement( 'canvas' );
    c.width = Math.ceil( W * scale );
    c.height = Math.ceil( H * scale );
    const g = c.getContext( '2d' )!;
    g.scale( scale, scale );
    const base = H * 0.76;
    const body = g.createLinearGradient( 0, base, 0, H );
    body.addColorStop( 0, this.rgba( this.mix( lavender, plum, 0.5 ), 0 ) );
    body.addColorStop( 0.2, this.rgba( this.mix( this.mix( lavender, pink, 0.3 ), night, 0.35 ), 0.97 ) );
    body.addColorStop( 1, this.rgba( this.mix( this.mix( lavender, plum, 0.6 ), night, 0.55 ), 1 ) );
    g.fillStyle = body;
    g.fillRect( 0, base, W, H - base );
    const lit = this.mix( this.mix( pink, gold, 0.35 ), [ 255, 240, 220 ], 0.15 );
    const shade = this.mix( this.mix( lavender, pink, 0.25 ), night, 0.25 );
    // dense (owner)
    for ( let i = 0; i < 130; i++ ) {
      const x = rand() * W;
      const y = base - H * 0.03 + Math.pow( rand(), 0.7 ) * H * 0.18;
      const rx = ( 120 + rand() * 220 ) * k;
      const ry = ( 12 + rand() * 20 ) * k;
      const high = 1 - ( y - base + H * 0.03 ) / ( H * 0.18 );
      for ( const dx of [ -W, 0, W ] ) {
        g.save();
        g.translate( x + dx, y );
        g.scale( rx, ry );
        const cg = g.createRadialGradient( 0, -0.35, 0, 0, 0, 1 );
        cg.addColorStop( 0, this.rgba( this.mix( shade, lit, 0.3 + 0.6 * high ), 0.8 ) );
        cg.addColorStop( 0.6, this.rgba( shade, 0.5 ) );
        cg.addColorStop( 1, this.rgba( shade, 0 ) );
        g.fillStyle = cg;
        g.fillRect( -1, -1, 2, 2 );
        g.restore();
      }
    }
    this.clouds = c;
  }

  /** The wallpaper: the rain all down the page (owner: raindrops, not the wisps that read as fog), still. */
  private bakeRainWall( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const rand = this.seededLocal( this.seedScene + 7 );
    const [ , , , pink, lavender ] = this.five();
    const tone = this.mix( this.mix( lavender, pink, 0.3 ), [ 255, 255, 255 ], 0.55 );
    const c = document.createElement( 'canvas' );
    c.width = Math.ceil( W * scale );
    c.height = Math.ceil( H * scale );
    const g = c.getContext( '2d' )!;
    g.scale( scale, scale );
    const slant = 0.18;
    g.strokeStyle = this.rgba( tone, 1 );
    g.lineWidth = Math.max( 0.8, 1.1 * k );
    g.lineCap = 'round';
    const n = Math.round( W * H / 9000 );
    for ( let i = 0; i < n; i++ ) {
      const x = rand() * W;
      const y = rand() * H;
      const len = ( 14 + rand() * 22 ) * k;
      g.globalAlpha = 0.1 + rand() * 0.18;
      g.beginPath();
      g.moveTo( x, y );
      g.lineTo( x - len * slant, y - len );
      g.stroke();
    }
    this.back = c;
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Roraima's own: plum, terracotta, gold, pink, lavender. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 50, 38, 40 ], [ 209, 116, 88 ], [ 221, 190, 109 ], [ 237, 139, 139 ], [ 143, 120, 183 ] ];
  }
}
